"use client"

import type { Blockchain } from "@anaconda/core/blockchain/types"
import { reatomComponent } from "@reatom/react"
import { ScopeProvider, useMolecule } from "bunshi/react"
import { type PropsWithChildren, useMemo } from "react"
import { BlockchainsMoleculeReatom } from "../blockchains/blockchain-molecule.reatom"
import { WalletConnectMoleculeReatom } from "../wallet-connect/store.reatom"
import {
  AnacondaEVMSDKScope,
  BlockchainBalanceSDKScope,
} from "./anaconda-sdk-scope"
import { useBlockchainBalanceSDKProviderReatom } from "./blockchains/blockchain-balance-sdk-provider.reatom"
import { useEvmSdkProviderReatom } from "./blockchains/evm-sdk-provider.reatom"
import { useSolanaSdkProviderReatom } from "./blockchains/solana-sdk-provider.reatom"

export interface EclipseScanner {
  getTransactionLink(txId: string): string
}

export const AnacondaSDKProviderReatom = reatomComponent(
  ({ children }: PropsWithChildren<object>) => {
    const evmSdk = useEvmSdkProviderReatom()
    useSolanaSdkProviderReatom()

    const { balanceSDK } = useBlockchainBalanceSDKProviderReatom()

    return (
      <ScopeProvider scope={AnacondaEVMSDKScope} value={evmSdk}>
        <ScopeProvider scope={BlockchainBalanceSDKScope} value={balanceSDK}>
          {/* Eclipse support removed */}
          {children}
        </ScopeProvider>
      </ScopeProvider>
    )
  },
  "AnacondaSDKProviderReatom",
)

export function useScannerReatom(blockchain?: Blockchain) {
  const { reatomScannerLinkByChainId, reatomScannerLinkByBlockchain } =
    useMolecule(BlockchainsMoleculeReatom)

  const { walletBlockchainNetworkId } = useMolecule(WalletConnectMoleculeReatom)

  // Call atoms directly - requires parent component to be wrapped with reatomComponent
  const network = walletBlockchainNetworkId()

  const scannerLink = blockchain
    ? reatomScannerLinkByBlockchain()(blockchain)
    : reatomScannerLinkByChainId()(network as number)

  const scanner = useMemo(() => {
    return {
      getTransactionLink(txId: string) {
        return `${scannerLink}/tx/${txId}`
      },
    }
  }, [scannerLink])

  return scanner
}
